import * as _ from 'underscore';

import { ListResult } from '../fetchers/ListResult';
import { ILayout, ILayoutMetrics } from '../models/ILayout';
import { ISize } from '../models/ISize';
import { ISliceable } from '../models/ISliceable';

export interface IScrollerScope extends ng.IScope {
  items: ISliceable<any>;
  layout: ILayout;
  scrollControls: IScrollControls;
  sliceChanged: (locals: { newSlice: ListResult<any>; }) => any;
  scrollLeftChanged: (locals: { scrollLeft: number }) => any;

  scroller: ScrollCtrl;
  slice: ListResult<any>;
  transcludeFn: ng.ITranscludeFunction;
  getViewportDimensions: () => ISize;
}

export interface IScrollControls {
  scrollIntoView: (startIndex: number, endIndex?: number) => void;
  setScrollLeft: (scrollLeft: number) => any;
  getScrollLeft: () => number;
}

interface ISliceRequest {
  page: number;
  itemsPerPage: number;
}

export class ScrollCtrl {

  public static ItemsChangeEvent = 'scrollItemsChanged';

  public static $inject = [
    '$scope', '$element', '$q', '$transclude'
  ];

  public content: JQuery;
  public contentArea: JQuery;

  private scope: IScrollerScope;
  private viewport: JQuery;
  private $q: ng.IQService;
  private metrics: ILayoutMetrics;
  private currentPage: number;
  private currentItemsPerPage: number;
  private currentTotal: number;
  private mostRecentInProgressRequest: ISliceRequest;

  constructor(
    $scope: IScrollerScope,
    $element: JQuery,
    $q: ng.IQService,
    $transclude: ng.ITranscludeFunction
  ) {
    this.scope = $scope;
    this.viewport = $element;
    this.$q = $q;
    this.currentPage = 0;
    this.currentItemsPerPage = 0;
    this.currentTotal = 0;

    $scope.scroller = this;
    $scope.transcludeFn = $transclude;
    $scope.slice = ListResult.empty();

    $scope.scrollControls = {

      scrollIntoView: (startIndex: number, endIndex?: number) => {

        if (!_.isNumber(endIndex)) {
          endIndex = startIndex;
        }

        // TODO: make this work for thumbnail view
        const firstVisibleIndex = Math.ceil(this.viewport.scrollTop() / this.metrics.heightOfRowInPx);
        const lastVisibleIndex = Math.floor((this.viewport.scrollTop() + this.viewport.height()) / this.metrics.heightOfRowInPx) - 1;

        if (endIndex > lastVisibleIndex) {
          this.viewport.scrollTop(((endIndex + 1) * this.metrics.heightOfRowInPx) - this.viewport.height());
        } else if (startIndex < firstVisibleIndex) {
          this.viewport.scrollTop(startIndex * this.metrics.heightOfRowInPx);
        }

      },

      setScrollLeft: (scrollLeft: number) => {
        this.viewport.scrollLeft(scrollLeft);
      },

      getScrollLeft: () => {
        return this.viewport.scrollLeft();
      }

    };

    let currentScrollLeft = 0;
    const updateScrollLeft = () => {
      const newScrollLeft = this.viewport.scrollLeft();
      if (newScrollLeft !== currentScrollLeft) {
        currentScrollLeft = newScrollLeft;
        $scope.scrollLeftChanged({scrollLeft:currentScrollLeft});
      }
    };

    $element.on('scroll', () => {
      updateScrollLeft();
      this.fetchItems();
    });

    // $note-ltan-2014-9-16:
    // Beware, watch does not know anything about the actual data. So even if the actual data changed,
    // but 'items' aka sliceable did not, watch callback won't get triggered and you don't get the update.
    // The trick is to create and assign a new instance of sliceable to the watched reference.
    $scope.$watch('items', (newItems: ISliceable<any>, oldItems: ISliceable<any>) => {
      // Both this watch and the viewport dimensions watch will trigger a fetch on initial load.
      // To avoid two fetches, we only trigger this one on subsequent changes.
      if (newItems !== oldItems) {
        this.onItemsChanged();
      }
    });

    $scope.getViewportDimensions = () => this.getViewportDimensions();

    $scope.$watch($scope.getViewportDimensions, (dimensions: ISize) => {
      this.resize(dimensions);
    }, true);
  }

  private fetchItems() {
    if (!this.metrics) return;
    if (this.viewport.scrollTop() < 0) return; // Handle iOS momentum scrolling beyond the top of container

    const pageHeight = ScrollCtrl.pageHeightInPx(this.metrics);
    if (pageHeight > 0) {
      const request = {
        page: Math.floor(this.viewport.scrollTop() / pageHeight),
        itemsPerPage: ScrollCtrl.itemsPerPage(this.metrics)
      };
      if ((request.page !== this.currentPage || request.itemsPerPage !== this.currentItemsPerPage)
          && !this.isInProgress(request)) {
        this.getSlice(request);
      }
    }
  }

  private onItemsChanged() {
    if (!this.metrics && !this.updateLayoutMetrics())
      return;

    const request = {
      page: 0,
      itemsPerPage: ScrollCtrl.itemsPerPage(this.metrics)
    };

    this.getSlice(request).then((result: ListResult<any>) => {
      if (result) {
        // Scroll to the top because we are displaying a new set of results.
        this.viewport.scrollTop(0);
        this.scope.$emit(ScrollCtrl.ItemsChangeEvent);
      }
    });
  }

  private resize(dimensions: ISize) {
    // Don't bother to fetch anything if it won't be visible.
    // (This can happen on initial load.)
    if (dimensions.width === 0 || dimensions.height === 0) return;
    this.metrics = this.getLayoutMetrics(dimensions);
    this.fetchItems();
  }

  public contentAreaHeight(): number {
    if (!this.metrics) return 0;
    return this.totalNumberOfRows(this.metrics) * this.metrics.heightOfRowInPx;
  }

  public contentTop(): number {
    if (!this.metrics) return 0;
    return this.currentPage * ScrollCtrl.pageHeightInPx(this.metrics);
  }

  private getSlice(request: ISliceRequest): ng.IPromise<ListResult<any>> {
    if (this.scope.items) {
      this.mostRecentInProgressRequest = request;
      // get two pages worth of items
      return this.scope.items.getSlice(request.page * request.itemsPerPage, request.itemsPerPage * 2).then((result: ListResult<any>) => {
        if (request === this.mostRecentInProgressRequest) {
          this.mostRecentInProgressRequest = null;
          this.currentPage = request.page;
          this.currentItemsPerPage = request.itemsPerPage;
          this.currentTotal = result.total;
          this.scope.slice.items = result.items;
          this.scope.slice.index = result.index;
          this.scope.slice.total = result.total;
          if (this.scope.sliceChanged) {
            this.scope.sliceChanged({ newSlice: result });
          }
          return result;
        } else {
          return null;
        }
      });
    } else {
      return this.$q.when(null);
    }
  }

  private isInProgress(request: ISliceRequest): boolean {
    return !!this.mostRecentInProgressRequest
        && request.page === this.mostRecentInProgressRequest.page
        && request.itemsPerPage === this.mostRecentInProgressRequest.itemsPerPage;
  }

  private getViewportDimensions(): ISize {
    return {
      height: this.viewport.height(),
      width: this.contentArea.width()
    };
  }

  private getLayoutMetrics(dimensions: ISize): ILayoutMetrics {
    if (!this.scope.layout) return null;
    return this.scope.layout.metrics(dimensions);
  }

  private updateLayoutMetrics(): boolean {
    const dimensions = this.getViewportDimensions();
    this.metrics = this.getLayoutMetrics(dimensions);
    return !!this.metrics;
  }

  private totalNumberOfRows(metrics: ILayoutMetrics): number {
    return metrics.numberOfCols > 0 ? Math.ceil(this.currentTotal / metrics.numberOfCols) : 0;
  }

  private static pageHeightInPx(metrics: ILayoutMetrics): number {
    return metrics.numberOfRows * metrics.heightOfRowInPx;
  }

  private static itemsPerPage(metrics: ILayoutMetrics): number {
    return metrics.numberOfCols * metrics.numberOfRows;
  }

}
